/**
 * airportsData — fetch + cache the Betty `Airports` model for the smart picker's air mode.
 * Direct GraphQL read (mirrors entities.js getAllCustomBroker), paginated. Mapped to the
 * picker's port-like shape so the same resolver/UI works for airports (Name=airportName,
 * portCode=IATA), plus `city` + `cityIden` for Google-Flights-style city grouping.
 */
import axios from 'axios';
import { BASE_API } from './entities';

const PAGE_SIZE = 100; // Betty caps `take`
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h
const cacheKey = (isUserPresent) => `pf_airports_cache_v${CACHE_VERSION}_${isUserPresent ? 'user' : 'public'}`;

const memoryCache = new Map(); // isUserPresent → mapped airports (shared across instances)

const readCache = (isUserPresent) => {
  if (memoryCache.has(isUserPresent)) return memoryCache.get(isUserPresent);
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(cacheKey(isUserPresent));
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || Date.now() - ts > CACHE_TTL_MS) return null;
    memoryCache.set(isUserPresent, data);
    return data;
  } catch (e) {
    return null; // corrupt/unavailable cache — fall through to a fresh fetch
  }
};

const writeCache = (isUserPresent, data) => {
  memoryCache.set(isUserPresent, data);
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(cacheKey(isUserPresent), JSON.stringify({ ts: Date.now(), data }));
    }
  } catch (e) {
    /* quota / unavailable — caching is best-effort */
  }
};

const toAirport = (a) => ({
  id: a.id,
  Name: a.airportName || a.city || a.code,
  Country: null, // Airports model carries no country directly (via cityIden → Company)
  CountryId: null,
  portCode: a.code || null, // IATA
  city: a.city || null,
  cityIden: a.cityIden != null ? a.cityIden : null,
  latitude: a.latitude != null ? a.latitude : null,
  longitude: a.longitude != null ? a.longitude : null,
});

/**
 * @returns {Promise<Array>} mapped airports (port-like shape). [] on error (picker
 *          degrades to places-only for air).
 */
// Netlify-prebaked static asset (built periodically) → mapped airports array.
const PREBAKED_AIRPORTS_URL = `${process.env.PUBLIC_URL || ''}/geo/airports.json`;
const readPrebakedAirports = async () => {
  try {
    const res = await axios.get(PREBAKED_AIRPORTS_URL, { timeout: 8000 });
    return Array.isArray(res?.data) ? res.data : null;
  } catch (e) {
    return null; // not deployed / not built yet — fall through to live read
  }
};

export const get_all_airports_from_db = async (jwt_token, isUserPresent) => {
  const cached = readCache(isUserPresent);
  if (cached) return cached;

  // cold-start: prebaked static asset seeds the cache before any live read.
  const prebaked = await readPrebakedAirports();
  if (prebaked?.length) {
    writeCache(isUserPresent, prebaked);
    return prebaked;
  }
  if (!jwt_token) return [];

  const headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + jwt_token };
  const all = [];
  for (let skip = 0; ; skip += PAGE_SIZE) {
    const query = `{ allAirports(take: ${PAGE_SIZE}, skip: ${skip}) {
      results { id code airportName city cityIden latitude longitude }
    } }`;
    let res;
    try {
      res = await axios.post(BASE_API, { query }, { headers });
    } catch (e) {
      return all.length ? all.map(toAirport) : [];
    }
    const results = res?.data?.data?.allAirports?.results;
    if (!Array.isArray(results)) break;
    all.push(...results);
    if (results.length < PAGE_SIZE) break;
  }

  const mapped = all.map(toAirport);
  if (mapped.length) writeCache(isUserPresent, mapped);
  return mapped;
};
